// HIA.1 — Painel de homologação controlada do Consultor IA.
// Sem conteúdo textual: apenas métricas agregadas.

import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { AlertOctagon, CheckCircle2, Gauge, TrendingUp, Users, DollarSign } from "lucide-react";
import { PageHeader, DataCard } from "@/components/admin/AdminPrimitives";
import { getHomologationOverview, type HomologationOverview } from "@/lib/ai/homologation.functions";

export const Route = createFileRoute("/_authenticated/admin/ia/homologacao")({
  head: () => ({
    meta: [
      { title: "Homologação IA — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: HomologacaoPage,
});

const WINDOWS = [
  { d: 7, label: "7 dias" },
  { d: 14, label: "14 dias" },
  { d: 30, label: "30 dias" },
];

const DECISION_STYLE: Record<HomologationOverview["decision"]["state"], string> = {
  not_started: "bg-black/5 text-[var(--gray-ink)]",
  in_homologation: "bg-blue-50 text-blue-800",
  healthy: "bg-emerald-50 text-emerald-800",
  attention: "bg-amber-50 text-amber-800",
  blocked: "bg-red-50 text-red-800",
  ready_for_expansion: "bg-[var(--green)]/12 text-[var(--green)]",
};

const DECISION_LABEL: Record<HomologationOverview["decision"]["state"], string> = {
  not_started: "Não iniciado",
  in_homologation: "Em homologação",
  healthy: "Saudável",
  attention: "Atenção",
  blocked: "Bloqueado",
  ready_for_expansion: "Pronto para expansão",
};

function pct(n: number) { return `${Math.round(n * 100)}%`; }
function fmtInt(n: number) { return n.toLocaleString("pt-BR"); }
function fmtMs(n: number | null) { return n == null ? "—" : n >= 1000 ? `${(n / 1000).toFixed(1)} s` : `${Math.round(n)} ms`; }
function fmtDelta(n: number | null) { return n == null ? "—" : (n > 0 ? `+${n.toFixed(1)}` : n.toFixed(1)); }

function Metric({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
        {icon}<span>{label}</span>
      </div>
      <p className="mt-1 text-xl font-semibold text-[var(--navy)]">{value}</p>
      {sub && <p className="text-[10px] text-[var(--gray-ink)]">{sub}</p>}
    </div>
  );
}

function HomologacaoPage() {
  const [days, setDays] = useState(14);
  const load = useServerFn(getHomologationOverview);
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["ai-homologation-overview", days],
    queryFn: () => load({ data: { days } }),
    staleTime: 60_000,
  });

  return (
    <div>
      <PageHeader
        title="Homologação do Consultor IA"
        description="Rollout controlado, adoção, utilidade, resultado e operação. Sem conteúdo privado."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {WINDOWS.map((w) => (
          <button
            key={w.d}
            onClick={() => setDays(w.d)}
            className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition ${
              days === w.d
                ? "border-[var(--navy)] bg-[var(--navy)] text-white"
                : "border-black/10 bg-white text-[var(--navy)] hover:bg-black/[0.03]"
            }`}
          >
            {w.label}
          </button>
        ))}
        {isFetching && <span className="text-[11px] text-[var(--gray-ink)]">Atualizando…</span>}
      </div>

      {isLoading && <p className="text-sm text-[var(--gray-ink)]">Carregando…</p>}
      {error && <p className="text-sm text-red-700">Erro ao carregar: {(error as Error).message}</p>}

      {data && (
        <>
          <div className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl px-4 py-3 ${DECISION_STYLE[data.decision.state]}`}>
            <div className="flex items-center gap-2">
              {data.decision.state === "blocked" ? <AlertOctagon size={16} /> : <CheckCircle2 size={16} />}
              <span className="text-sm font-semibold">Decisão: {DECISION_LABEL[data.decision.state]}</span>
            </div>
            {data.decision.missing.length > 0 && (
              <p className="text-[11px]">Critérios faltando: {data.decision.missing.join(" · ")}</p>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <DataCard><Metric icon={<Users size={14} />} label="Coortes ativas" value={fmtInt(data.cohorts.active)} sub={`${fmtInt(data.cohorts.total)} no período`} /></DataCard>
            <DataCard><Metric icon={<TrendingUp size={14} />} label="Taxa de aplicação" value={pct(data.utility.applyRate)} sub={`${fmtInt(data.utility.applied + data.utility.appliedAfterEdit)} aplicadas`} /></DataCard>
            <DataCard><Metric icon={<Gauge size={14} />} label="Latência p95" value={fmtMs(data.operation.p95LatencyMs)} sub={`${fmtInt(data.operation.llmErrors)} erros · ${fmtInt(data.operation.llmTimeouts)} timeouts`} /></DataCard>
            <DataCard><Metric icon={<DollarSign size={14} />} label="Custo (USD)" value={data.operation.costUsd >= 1 ? `$${data.operation.costUsd.toFixed(2)}` : `$${data.operation.costUsd.toFixed(4)}`} sub={`janela ${data.windowDays}d`} /></DataCard>
          </div>

          <section className="mt-6 rounded-xl border border-black/5 bg-white p-4">
            <h3 className="text-sm font-semibold text-[var(--navy)]">Adoção</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-4 text-xs">
              <div><p className="text-[var(--gray-ink)]">Elegíveis</p><p className="text-lg font-semibold text-[var(--navy)]">{fmtInt(data.adoption.eligibleUsers)}</p></div>
              <div><p className="text-[var(--gray-ink)]">Abriram</p><p className="text-lg font-semibold text-[var(--navy)]">{fmtInt(data.adoption.openedUsers)}</p></div>
              <div><p className="text-[var(--gray-ink)]">Enviaram mensagem</p><p className="text-lg font-semibold text-[var(--navy)]">{fmtInt(data.adoption.messagedUsers)}</p></div>
              <div><p className="text-[var(--gray-ink)]">Aplicaram sugestão</p><p className="text-lg font-semibold text-[var(--navy)]">{fmtInt(data.adoption.appliedUsers)}</p></div>
            </div>
          </section>

          <section className="mt-4 rounded-xl border border-black/5 bg-white p-4">
            <h3 className="text-sm font-semibold text-[var(--navy)]">Utilidade e resultado</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-3 text-xs">
              <div><p className="text-[var(--gray-ink)]">Rejeição</p><p className="text-lg font-semibold text-[var(--navy)]">{pct(data.utility.rejectRate)}</p></div>
              <div><p className="text-[var(--gray-ink)]">Regenerações</p><p className="text-lg font-semibold text-[var(--navy)]">{fmtInt(data.utility.regenerated)}</p></div>
              <div><p className="text-[var(--gray-ink)]">Feedback útil</p><p className="text-lg font-semibold text-[var(--navy)]">{pct(data.utility.usefulFeedbackRate)}</p></div>
              <div><p className="text-[var(--gray-ink)]">Δ Completude médio</p><p className="text-lg font-semibold text-[var(--navy)]">{fmtDelta(data.outcome.avgCompletenessDelta)}</p></div>
              <div><p className="text-[var(--gray-ink)]">Δ Pendências médio</p><p className="text-lg font-semibold text-[var(--navy)]">{fmtDelta(data.outcome.avgPendingDelta)}</p></div>
              <div><p className="text-[var(--gray-ink)]">Coortes finalizadas</p><p className="text-lg font-semibold text-[var(--navy)]">{fmtInt(data.outcome.finishedCohorts)}</p></div>
            </div>
          </section>

          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <section className="rounded-xl border border-black/5 bg-white p-4">
              <h3 className="text-sm font-semibold text-[var(--navy)]">Motivos de rejeição</h3>
              {data.rejectReasons.length === 0 ? (
                <p className="mt-2 text-xs text-[var(--gray-ink)]">Sem rejeições classificadas.</p>
              ) : (
                <ul className="mt-2 divide-y divide-black/5 text-xs">
                  {data.rejectReasons.map((r) => (
                    <li key={r.reason} className="flex items-center justify-between py-1.5">
                      <span className="text-[var(--navy)]">{r.reason}</span>
                      <span className="font-semibold">{r.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-xl border border-black/5 bg-white p-4">
              <h3 className="text-sm font-semibold text-[var(--navy)]">Feedback por gatilho</h3>
              {data.feedbackByTrigger.length === 0 ? (
                <p className="mt-2 text-xs text-[var(--gray-ink)]">Sem feedback ainda.</p>
              ) : (
                <ul className="mt-2 divide-y divide-black/5 text-xs">
                  {data.feedbackByTrigger.map((f) => (
                    <li key={f.trigger} className="flex items-center justify-between py-1.5">
                      <span className="text-[var(--navy)]">{f.trigger}</span>
                      <span className="text-[var(--gray-ink)]">
                        <span className="font-semibold text-emerald-700">{f.useful}</span> · <span className="font-semibold text-red-700">{f.notUseful}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <section className="mt-4 rounded-xl border border-black/5 bg-white p-4">
            <h3 className="text-sm font-semibold text-[var(--navy)]">Desempenho por seção</h3>
            {data.bySection.length === 0 ? (
              <p className="mt-2 text-xs text-[var(--gray-ink)]">Sem dados no período.</p>
            ) : (
              <div className="mt-2 overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-[var(--gray-ink)]">
                      <th className="py-1.5 pr-3">Seção</th>
                      <th className="py-1.5 pr-3">Sugestões</th>
                      <th className="py-1.5 pr-3">Aplicadas</th>
                      <th className="py-1.5 pr-3">Rejeitadas</th>
                      <th className="py-1.5 pr-3">Taxa</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5">
                    {data.bySection.map((s) => (
                      <tr key={s.section} className="text-[var(--navy)]">
                        <td className="py-1.5 pr-3">{s.section}</td>
                        <td className="py-1.5 pr-3">{s.suggestions}</td>
                        <td className="py-1.5 pr-3">{s.applied}</td>
                        <td className="py-1.5 pr-3">{s.rejected}</td>
                        <td className="py-1.5 pr-3">{s.suggestions > 0 ? pct(s.applied / s.suggestions) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <p className="mt-4 text-[10px] text-[var(--gray-ink)]">
            Privacidade: este painel exibe apenas métricas agregadas. Nenhum texto de conversa, sugestão ou plano é exposto.
          </p>
        </>
      )}
    </div>
  );
}
